import React, { useEffect, useState } from "react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell, PieChart, Pie } from "recharts";
import api from "@/lib/api";
import { PageHeader, Loader, StatCard } from "@/components/kit";

export default function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setA(data)).catch(() => {}); }, []);
  if (!a) return <Loader />;
  const profitData = (a.most_profitable_dishes || []).map((d) => ({ name: d.name?.slice(0, 14), profit: d.profit }));
  const costData = (a.highest_cost_dishes || []).map((d) => ({ name: d.name?.slice(0, 14), cost: d.cost }));
  const split = [{ name: "Cost", value: a.projected_ingredient_cost }, { name: "Profit", value: a.projected_gross_profit }];
  const COLORS = ["#CD7F32", "#D4AF37"];

  return (
    <div>
      <PageHeader eyebrow="Intelligence" title="Analytics" subtitle="Projected performance across your menus. Distinct from realized financials." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Menu Revenue Potential" value={`$${a.menu_revenue_potential.toLocaleString()}`} accent />
        <StatCard label="Projected Ingredient Cost" value={`$${a.projected_ingredient_cost.toLocaleString()}`} />
        <StatCard label="Projected Gross Profit" value={`$${a.projected_gross_profit.toLocaleString()}`} accent />
        <StatCard label="Food Cost" value={`${a.food_cost_pct}%`} />
      </div>
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="matte-card p-6 lg:col-span-2">
          <p className="label-eyebrow mb-5">Most Profitable Dishes</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={profitData}>
              <XAxis dataKey="name" tick={{ fill: "#666", fontSize: 11 }} />
              <YAxis tick={{ fill: "#666", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 2 }} />
              <Bar dataKey="profit" radius={[3, 3, 0, 0]}>{profitData.map((_, i) => <Cell key={i} fill="#D4AF37" />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="matte-card p-6">
          <p className="label-eyebrow mb-5">Cost vs Profit</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={split} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {split.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 2 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-5 text-sm">
            <span className="flex items-center gap-2 text-white/60"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#CD7F32" }} /> Cost</span>
            <span className="flex items-center gap-2 text-white/60"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#D4AF37" }} /> Profit</span>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="matte-card p-6">
          <p className="label-eyebrow mb-4">Highest Cost Dishes</p>
          {costData.length === 0 && <p className="text-white/40 text-sm">No data yet.</p>}
          <div className="space-y-3">{(a.highest_cost_dishes || []).map((d, i) => (
            <div key={i} className="flex items-center justify-between text-sm"><span className="text-white/80">{d.name}</span><span className="font-mono text-gold">${d.cost}</span></div>
          ))}</div>
        </div>
        <div className="grid grid-cols-2 gap-4 content-start">
          <StatCard label="Recipes Created" value={a.recipes_created} />
          <StatCard label="Menus Created" value={a.menus_created} />
          <StatCard label="Inventory Value" value={`$${a.inventory_value.toLocaleString()}`} />
          <StatCard label="Supplier Spend" value={`$${a.supplier_spend.toLocaleString()}`} />
        </div>
      </div>
    </div>
  );
}
